/**
 * Centralized user-based visibility and restriction checks.
 * Add new user IDs or checks here so they stay in one place.
 *
 * USAGE:
 * ------
 * 1. In any component that receives `user` from Redux/props:
 *
 *    import { shouldHideSupplyChains, hasRestrictedGroupForDeferralMenu } from "constants/user-visibility";
 *
 *    // Hide something when user is in the supply-chains hide list:
 *    { shouldHideSupplyChains(this.props.user) ? null : <SupplyChainsTable /> }
 *
 *    // Hide menu items when user has restricted groups (7, 11, 16):
 *    if (hasRestrictedGroupForDeferralMenu(this.props.user)) { ... }
 *
 * 2. To add a new check: add a new array (e.g. SOME_FEATURE_USER_IDS) and a
 *    helper (e.g. shouldHideSomeFeature(user)) that uses getUserId(user), then
 *    use that helper in your component.
 *
 * JSDoc (the @param and @returns lines) are optional documentation:
 *   @param {Object} user - describes the function's argument
 *   @returns {boolean} - describes what the function returns
 * They help IDEs show tooltips and improve autocomplete; they are not required to run.
 */

/**
 * Get current user id from user object (supports Immutable and plain object).
 * @param {Object} user - User object from props (may be Immutable Map or plain object)
 * @returns {number|null} User id or null
 */
export const getUserId = (user) => {
    if (!user) return null;
    // If user has getIn (Immutable Map): try getIn(["data","id"]) then get("id"). Else (plain object): use user.id.
    // We never call user.getIn() when user.getIn is undefined — the ternary picks the other branch.
    const id = user.getIn
        ? (user.getIn(["data", "id"]) ?? user.get("id"))
        : user.id;
    return id != null ? Number(id) : null;
};

/**
 * User IDs for whom the Supply Chains table (Account Facility) is hidden.
 * Add or remove IDs here for future changes.
 */
export const HIDE_SUPPLY_CHAINS_USER_IDS = [
    183438,185921
];

/**
 * Whether to hide the Supply Chains table for the given user.
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const shouldHideSupplyChains = (user) => {
    const userId = getUserId(user);
    return userId != null && HIDE_SUPPLY_CHAINS_USER_IDS.includes(userId);
};

/**
 * Group IDs (7, 11, 16) - users in these groups get db_onboarding and anchor_onboarding
 * removed from header menu. Used in header-menu.js
 */
export const RESTRICTED_GROUP_IDS_FOR_DEFERRAL_MENU = [7, 11, 16];

/**
 * Whether the user has a restricted group (for hiding db_onboarding / anchor_onboarding in header).
 * @param {Object} user - User object from props (with get("groups"))
 * @returns {boolean}
 */
export const hasRestrictedGroupForDeferralMenu = (user) => {
    const groups = user?.getIn?.(["data", "groups"]) ?? user?.get?.("groups");
    if (!groups || !groups.some) return false;
    return groups.some((group) => {
        const groupId = group?.id ?? group?.get?.("id");
        return RESTRICTED_GROUP_IDS_FOR_DEFERRAL_MENU.includes(Number(groupId));
    });
};
